import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Autocomplete, type AutocompleteOption } from "@/components/ui/autocomplete";
import { apiRequest } from "@/lib/queryClient";

export type CrmPickerEntity = "company" | "contact" | "branch" | "lead" | "deal" | "brand" | "synergy";

interface CrmQueryItem {
  id: string;
  title: string;
  subtitle?: string;
}

const DETAIL_ENDPOINTS: Partial<Record<CrmPickerEntity, (id: string) => string>> = {
  company: (id) => `/api/companies/${id}`,
  contact: (id) => `/api/contacts/${id}`,
  branch: (id) => `/api/branches/${id}`,
  lead: (id) => `/api/leads/${id}`,
  deal: (id) => `/api/deals/${id}`,
  brand: (id) => `/api/brands/${id}`,
  synergy: (id) => `/api/synergies/${id}`,
};

function unwrapDetailPayload(payload: any) {
  return payload?.data ?? payload?.record ?? payload?.item ?? payload;
}

function fullName(record: any) {
  return [record?.firstName ?? record?.first_name, record?.lastName ?? record?.last_name]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(" ");
}

function detailLabel(entityType: CrmPickerEntity, payload: any): string | undefined {
  const record = unwrapDetailPayload(payload);
  if (!record || typeof record !== "object") return undefined;
  if (entityType === "contact" || entityType === "lead") {
    const name = fullName(record) || record.name || record.title;
    const company = record.companyName ?? record.company_name ?? record.company?.name;
    return [name, company].map((part) => String(part ?? "").trim()).filter(Boolean).join(" · ") || undefined;
  }
  if (entityType === "deal") {
    return [record.name ?? record.title, record.companyName ?? record.company_name ?? record.company?.name]
      .map((part) => String(part ?? "").trim())
      .filter(Boolean)
      .join(" · ") || undefined;
  }
  if (entityType === "synergy") {
    return [record.entityName ?? record.entity_name ?? record.name, record.dealName ?? record.deal_name]
      .map((part) => String(part ?? "").trim())
      .filter(Boolean)
      .join(" · ") || undefined;
  }
  return String(record.name ?? record.title ?? "").trim() || undefined;
}

interface CrmRecordAutocompleteProps {
  entityType: CrmPickerEntity;
  value?: string;
  onValueChange: (value: string) => void;
  onOptionSelect?: (option: AutocompleteOption | null) => void;
  placeholder: string;
  searchPlaceholder?: string;
  selectedLabel?: string;
  initialOptions?: AutocompleteOption[];
  icon?: React.ReactNode;
  disabled?: boolean;
  id?: string;
  filters?: Array<{ field: string; operator: "eq"; value: string | number }>;
  /**
   * Azienda di contesto. Con entityType="contact" la tendina mostra, oltre alle
   * persone dell'azienda, quelle di TUTTA la catena di case madri, in sezioni
   * separate: nel retail il decisore sta spesso a un livello superiore.
   * Vale per ogni azienda controllata, non solo per quelle nate come filiali.
   */
  companyScopeId?: number | null;
  /**
   * Valori (come stringa, stessa forma di `value`) da non mostrare mai tra i
   * risultati: tipicamente un soggetto gia' presente altrove nello stesso
   * form con un ruolo diverso (es. il contatto/azienda gia' principale del
   * deal), per cui aggiungerlo di nuovo creerebbe un doppione visibile.
   */
  excludeIds?: Array<string | number>;
}

interface ContactGroup {
  companyId: number;
  companyName: string;
  depth: number;
  relationship: "direct" | "parent_company" | "group_company";
  label: string;
  contacts: Array<{
    id: number;
    firstName?: string | null;
    lastName?: string | null;
    role?: string | null;
    companyEmail?: string | null;
    privateEmail?: string | null;
  }>;
}

// Stable defaults keep the registry projection memoized while the user types.
const EMPTY_INITIAL_OPTIONS: AutocompleteOption[] = [];
const EMPTY_FILTERS: NonNullable<CrmRecordAutocompleteProps['filters']> = [];
const EMPTY_EXCLUDE_IDS: Array<string | number> = [];

export function CrmRecordAutocomplete({
  entityType,
  value,
  onValueChange,
  onOptionSelect,
  placeholder,
  searchPlaceholder = "Cerca in tutto il CRM...",
  selectedLabel,
  initialOptions = EMPTY_INITIAL_OPTIONS,
  icon,
  disabled,
  id,
  filters = EMPTY_FILTERS,
  companyScopeId,
  excludeIds = EMPTY_EXCLUDE_IDS,
}: CrmRecordAutocompleteProps) {
  const excludedValues = React.useMemo(() => new Set(excludeIds.map(String)), [excludeIds]);
  const [search, setSearch] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [resolvedSelection, setResolvedSelection] = React.useState<AutocompleteOption | null>(null);

  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  const { data, isFetching, isError, refetch } = useQuery<{ items: CrmQueryItem[] }>({
    queryKey: ["/api/crm-query/execute", "picker", entityType, debouncedSearch, filters],
    queryFn: ({ signal }) => apiRequest("/api/crm-query/execute", {
      method: "POST",
      // POST trasporta il filtro, ma e' una lettura: non puo' invalidare tutte
      // le query attive e rigenerare il proprio autocomplete in un loop.
      isMutation: false,
      notifyError: false,
      timeoutMs: 8_000,
      signal,
      data: {
        entityType,
        search: debouncedSearch,
        match: "all",
        filters,
        sorts: [],
        // Il picker usa solo id, titolo e sottotitolo: evita i JSON completi e
        // le sottoquery di dettaglio che servono alle schede, non alla ricerca.
        includeRecord: false,
        limit: 20,
        offset: 0,
      },
    }),
    enabled: debouncedSearch.length >= 2 && !disabled,
    retry: false,
  });

  const scopeEnabled = entityType === "contact" && Boolean(companyScopeId);
  const { data: scopeGroups } = useQuery<{ groups: ContactGroup[] }>({
    queryKey: [`/api/companies/${companyScopeId}/contact-groups`],
    queryFn: () => apiRequest(`/api/companies/${companyScopeId}/contact-groups`),
    enabled: scopeEnabled,
    staleTime: 60_000,
  });

  // Le persone della catena societaria diventano opzioni raggruppate: la sezione
  // e' l'azienda di provenienza, cosi' "diretto" e "casa madre" restano distinti
  // anche quando i nomi si somigliano.
  const scopedOptions = React.useMemo<AutocompleteOption[]>(() => {
    if (!scopeEnabled) return [];
    return (scopeGroups?.groups ?? []).flatMap((group) =>
      group.contacts.map((contact, index) => {
        const name = [contact.firstName, contact.lastName].filter(Boolean).join(" ").trim();
        const detail = [contact.role, group.relationship === "direct" ? null : group.companyName]
          .filter(Boolean)
          .join(" · ");
        return {
          value: String(contact.id),
          label: detail ? `${name || `Contatto #${contact.id}`} · ${detail}` : name || `Contatto #${contact.id}`,
          icon,
          group: group.label,
          groupHeader: index === 0,
          highlighted: group.relationship === "direct",
        };
      }),
    );
  }, [icon, scopeEnabled, scopeGroups]);

  const detailEndpoint = value ? DETAIL_ENDPOINTS[entityType]?.(value) : null;
  const { data: selectedDetail } = useQuery({
    queryKey: ["/api/crm-picker/detail", entityType, value],
    queryFn: () => apiRequest(detailEndpoint!),
    enabled: Boolean(value && !selectedLabel && detailEndpoint && !resolvedSelection),
    retry: false,
    staleTime: 60_000,
  });

  const resolvedSelectedLabel = selectedLabel || detailLabel(entityType, selectedDetail);

  // Context options change with the record/group, never with each keystroke.
  const baseOptions = React.useMemo<AutocompleteOption[]>(() => {
    // Con un'azienda di contesto la lista parte da filiale + casa madre, ma
    // mantiene anche gli eventuali contatti gia' caricati dal chiamante: una
    // persona esterna al gruppo puo' essere collegata senza dover digitare.
    // Le intestazioni sintetiche del vecchio elenco non sono opzioni reali.
    const scopedIds = new Set(scopedOptions.map((option) => option.value));
    const otherInitialOptions = initialOptions
      .filter((option) => !String(option.value).startsWith("_header_") && !scopedIds.has(option.value))
      .map((option) => ({
        ...option,
        group: "Altri contatti",
        groupHeader: false,
        highlighted: false,
      }));
    const base = scopedOptions.length > 0
      ? [...scopedOptions, ...otherInitialOptions]
      : initialOptions;
    return base;
  }, [scopedOptions, initialOptions]);

  const currentSelectionLabel = React.useMemo(() =>
    (resolvedSelection && resolvedSelection.value === value ? resolvedSelection.label : undefined)
      || resolvedSelectedLabel || baseOptions.find(option => option.value === value)?.label,
    [resolvedSelection, resolvedSelectedLabel, baseOptions, value]);

  const options = React.useMemo(() => {
    const selected: AutocompleteOption[] = value
      ? [resolvedSelection?.value === value
        ? resolvedSelection
        : { value, label: currentSelectionLabel || `Record #${value}`, icon }]
      : [];
    const remote: AutocompleteOption[] = (data?.items ?? []).map((item) => ({
      value: String(item.id), label: item.subtitle ? `${item.title} · ${item.subtitle}` : item.title, icon,
    }));
    const liveSearch = search.trim();
    // Until the debounced request belongs to the visible text, do not scan or
    // show the full local registry (nor results from the previous query).
    const source = liveSearch
      ? (liveSearch.length >= 2 && liveSearch === debouncedSearch ? remote : [])
      : [...selected, ...baseOptions];
    const seen = new Set<string>();
    const deduped = source.filter(option => {
      if (seen.has(option.value) || (excludedValues.has(option.value) && option.value !== value)) return false;
      seen.add(option.value);
      return true;
    });
    // Dopo la deduplica la prima riga di un gruppo puo' essere sparita: le
    // intestazioni vanno ricalcolate, altrimenti una sezione resta senza titolo.
    let lastGroup: string | undefined;
    return deduped.map((option) => {
      const isFirstOfGroup = Boolean(option.group) && option.group !== lastGroup;
      lastGroup = option.group;
      return option.group ? { ...option, groupHeader: isFirstOfGroup } : option;
    });
  }, [data?.items, search, debouncedSearch, excludedValues, icon, baseOptions, resolvedSelection, currentSelectionLabel, value]);

  const handleValueChange = (nextValue: string) => {
    if (disabled) return;
    const nextOption = options.find((option) => option.value === nextValue) ?? null;
    setResolvedSelection(nextOption);
    onOptionSelect?.(nextOption);
    onValueChange(nextValue);
  };

  return (
    <Autocomplete
      id={id}
      options={options}
      value={value}
      selectedLabel={currentSelectionLabel}
      onValueChange={handleValueChange}
      filterOptionsLocally={false}
      onSearchChange={setSearch}
      onOpenChange={(open) => { if (!open) { setSearch(""); setDebouncedSearch(""); } }}
      onRetry={() => { void refetch(); }}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      notFoundText={search.trim().length < 2 ? "Scrivi almeno 2 caratteri." : "Nessun record trovato."}
      loading={search.trim().length >= 2 && (search.trim() !== debouncedSearch || isFetching)}
      errorText={search.trim() === debouncedSearch && isError ? "Ricerca non disponibile. Riprova." : undefined}
      disabled={disabled}
      // Con la catena societaria le sezioni vanno mostrate SUBITO, senza dover
      // digitare: e' proprio l'elenco che l'utente si aspetta di vedere aprendo
      // il campo. Senza questo, i contatti della casa madre resterebbero
      // invisibili perche' non "highlighted".
      showUnhighlightedWithoutSearch={scopedOptions.length > 0}
    />
  );
}
